import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, UserPlus, LogIn, Building2, Check, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { clientTeamService, type ClientTeamInvitation } from '@/services/clientTeamService';
import { supabase } from '@/integrations/supabase/client';

export const ClientInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [invitation, setInvitation] = useState<ClientTeamInvitation | null>(null);
  const [clientInfo, setClientInfo] = useState<{ name: string; company?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  // Fetch client info even when not authenticated (for display purposes)
  useEffect(() => {
    const loadClientInfo = async () => {
      if (!token) return;
      
      try {
        // Try to get basic invitation info for display
        const { data, error } = await supabase
          .from('client_team_invitations')
          .select(`
            name,
            role,
            clients:client_id (
              name,
              company
            )
          `)
          .eq('invitation_token', token)
          .maybeSingle();
        
        if (!error && data?.clients) {
          setClientInfo({
            name: (data.clients as any).name || 'Client',
            company: (data.clients as any).company
          });
        }
      } catch (err) {
        console.error('Error loading client info:', err);
      }
    };

    loadClientInfo();
  }, [token]);

  // Fetch full invitation after user is authenticated
  useEffect(() => {
    const loadInvitation = async () => {
      if (!token || !user) return;

      setLoading(true);
      try {
        const invitationData = await clientTeamService.getInvitationByToken(token);
        if (!invitationData) {
          setError('Invitation not found or has expired');
        } else {
          setInvitation(invitationData);
          // Store token so we can show invitation on ClientDashboard
          localStorage.setItem('pending_team_invitation_token', token);
        }
      } catch (error) {
        console.error('Error loading invitation:', error);
        setError('Failed to load invitation details');
      } finally {
        setLoading(false);
      }
    };

    if (user && token) {
      loadInvitation();
    }
  }, [user, token]);

  const handleAcceptInvitation = async () => {
    if (!token || !user || !invitation) return;

    try {
      setAccepting(true);
      await clientTeamService.acceptInvitation(token, user.id);
      localStorage.removeItem('pending_team_invitation_token');
      toast.success('Welcome to the team! Redirecting to your portal...');
      
      // Faster redirect
      navigate('/client-dashboard');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
      setAccepting(false);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!token || !invitation) return;

    try {
      setDeclining(true);
      await clientTeamService.declineInvitation(token);
      localStorage.removeItem('pending_team_invitation_token');
      toast.success('Invitation declined');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error declining invitation:', error);
      toast.error(error.message || 'Failed to decline invitation');
      setDeclining(false);
    }
  };

  const handleGoToAuth = () => {
    // Store token in localStorage so it persists after auth
    // This is the KEY mechanism to show invitation after login
    if (token) {
      localStorage.setItem('pending_team_invitation_token', token);
    }
    // Navigate to auth - the useAuth hook will check for pending invitation
    // and redirect to client-dashboard after sign in
    navigate('/auth');
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user is NOT authenticated, show sign-in prompt with client info
  if (!user) {
    const displayClientName = clientInfo?.name || 'a client';
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                alt="Event Date Picker Logo" 
                className="h-12 w-auto" 
              />
            </div>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <Users className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl">Team Member Invitation</CardTitle>
            <CardDescription>
              You've been invited to join a client team!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4 text-center space-y-2">
              <p className="font-medium text-foreground">
                You have been invited to join <strong>{displayClientName}</strong>
                {clientInfo?.company && <span className="text-muted-foreground"> ({clientInfo.company})</span>} as a <strong>Team Member</strong>.
              </p>
              <p className="text-sm text-muted-foreground">
                Please sign in or create an account to accept.
              </p>
            </div>
            <Button onClick={handleGoToAuth} className="w-full" size="lg">
              <LogIn className="h-4 w-4 mr-2" />
              Sign In / Create Account
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              After signing in, you'll be able to accept or decline the invitation
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while fetching invitation
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading invitation...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show invitation details with Accept/Decline buttons
  if (invitation) {
    const clientName = invitation.clients?.name || 'Client';
    const clientCompany = invitation.clients?.company;

    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl">Team Member Invitation</CardTitle>
            <CardDescription>
              You've been invited to join a client team
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Client Details */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Client</span>
                <span className="font-semibold">{clientName}</span>
              </div>
              {clientCompany && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Company</span>
                  <span className="font-medium">{clientCompany}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Your Role</span>
                <Badge variant="secondary" className="capitalize">{invitation.role}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Invited As</span>
                <span className="font-medium">{invitation.name}</span>
              </div>
            </div>

            {/* Info message */}
            <p className="text-sm text-muted-foreground text-center">
              By accepting this invitation, you'll be able to view events and feedback for this client.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleDeclineInvitation}
                disabled={declining || accepting}
              >
                {declining ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <X className="h-4 w-4 mr-2" />
                )}
                Decline
              </Button>
              <Button
                className="flex-1"
                onClick={handleAcceptInvitation}
                disabled={accepting || declining}
              >
                {accepting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Accept
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};
